'use client';

import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';

import type { LayersList } from '@deck.gl/core';

export type DeckglOverlayProps = { layers?: LayersList };

/**
 * A very simple implementation of a component that renders a list of deck.gl layers
 * via the GoogleMapsOverlay into the <Map> component containing it.
 */
const MedMapDeckOverlay = ({ layers }: DeckglOverlayProps) => {
  const pathname = usePathname();
  const map = useMap();

  // Create a new overlay instance when the route changes to /search
  // This ensures proper reinitialization
  const deck = useMemo(() => {
    return new GoogleMapsOverlay({ interleaved: true });
  }, [pathname === '/search' ? pathname : null]);

  // add the overlay to the map once the map is available
  useEffect(() => {
    if (!deck || !map) return;

    deck.setMap(map);

    return () => {
      // Proper cleanup
      deck.setMap(null);
      deck.finalize();
    };
  }, [deck, map]);

  // whenever the rendered data changes, the layers will be updated
  useEffect(() => {
    if (!layers) return;
    deck.setProps({ layers });
  }, [deck, layers]);

  // no dom rendered by this component
  return null;
};

export default MedMapDeckOverlay;
